import { useState, useEffect } from "react";
import {
  ArrowLeft,
  Shield,
  Lock,
  Eye,
  Database,
  Share2,
  Globe,
  FileText,
  RefreshCw,
  MapPin,
  AlertCircle,
} from "lucide-react";


const SECTIONS = [
  { id: "info-collect", title: "1. What Info We Collect?", icon: Database },
  { id: "social-logins", title: "2. Social Logins", icon: Share2 },
  { id: "third-party", title: "3. Third-Party Websites", icon: Globe },
  { id: "retention", title: "4. Data Retention", icon: FileText },
  { id: "security", title: "5. Data Security", icon: Lock },
  { id: "rights", title: "6. Your Privacy Rights", icon: Shield },
  { id: "dnt", title: "7. Do-Not-Track Features", icon: Eye },
  { id: "california", title: "8. California Rights", icon: MapPin },
  { id: "updates", title: "9. Policy Updates", icon: RefreshCw },
  { id: "contact", title: "10. Contact Us", icon: AlertCircle },
];

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState("info-collect");

  // smooth scroll handler
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(id);
    }
  };

  // intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -50% 0px" },
    );

    SECTIONS.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 xl:px-6 sm:px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-slate-500 hover:text-blue-600 font-bold text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="text-sm font-medium text-slate-400 sm:text-xs">
            Last Updated:{" "}
            <span className="text-slate-900">October 24, 2024</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 xl:px-6 sm:px-8 py-12">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-5xl sm:text-4xl font-black tracking-tight mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg text-slate-500 leading-relaxed">
            <strong>GradConnect Innovations Private Limited</strong> ("we,"
            "our," or "us") respects your privacy. This policy explains how we
            collect, use, and protect your personal information when you use
            Freshment.
          </p>
        </div>

        <div className="flex flex-row sm:flex-col gap-12 relative">
          {/* Sidebar Navigation */}
          <div className="block sm:hidden w-80 flex-shrink-0">
            <div className="sticky top-24 bg-white rounded-2xl shadow-[0_2px_15px_rgba(0,0,0,0.03)] border border-slate-100 p-2 max-h-[calc(100vh-120px)] overflow-y-auto custom-scrollbar">
              {SECTIONS.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-bold transition-all duration-200 ${
                    activeSection === section.id
                      ? "bg-blue-50 text-blue-700 shadow-sm"
                      : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <section.icon
                    className={`w-4 h-4 ${activeSection === section.id ? "text-blue-600" : "text-slate-400"}`}
                  />
                  {section.title}
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 space-y-12 pb-24">
            {/* 1. What Info We Collect */}
            <section id="info-collect" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-2">
                1. What Information Do We Collect?
              </h2>

              <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm text-slate-600 leading-relaxed space-y-8">
                {/* Personal Info */}
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    Personal information you disclose to us
                  </h3>
                  <div className="bg-blue-50 p-4 rounded-xl mb-4 text-blue-800 text-sm font-medium border border-blue-100">
                    <strong>In Short:</strong> We collect information that you
                    provide to us.
                  </div>
                  <p className="mb-3">
                    We collect personal information that you voluntarily provide
                    to us when you express an interest in obtaining information
                    about us or our products and Services, when you participate
                    in activities on the Services or otherwise when you contact
                    us.
                  </p>
                  <p className="mb-3">
                    The personal information we collect may include the
                    following:
                  </p>
                  <ul className="list-disc pl-5 space-y-1 mb-3">
                    <li>
                      <strong>Personal Information Provided by You:</strong> We
                      collect names; email addresses; phone numbers; usernames;
                      passwords; and other similar information.
                    </li>
                    <li>
                      <strong>Social Media Login Data:</strong> We may provide
                      you with the option to register with us using your
                      existing social media account details, like your Facebook,
                      Twitter or other social media account.
                    </li>
                  </ul>
                </div>

                {/* Auto Collected */}
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    Information automatically collected
                  </h3>
                  <div className="bg-blue-50 p-4 rounded-xl mb-4 text-blue-800 text-sm font-medium border border-blue-100">
                    <strong>In Short:</strong> Some information — such as your
                    Internet Protocol (IP) address and/or browser and device
                    characteristics — is collected automatically when you visit
                    our Services.
                  </div>
                  <p className="mb-3">
                    We automatically collect certain information when you visit,
                    use or navigate the Services. This information does not
                    reveal your specific identity (like your name or contact
                    information) but may include device and usage information,
                    such as your IP address, browser and device characteristics,
                    operating system, language preferences, referring URLs,
                    device name, country, location, information about who and
                    when you use our Services and other technical information.
                  </p>
                  <p className="mb-2 font-bold text-slate-800">
                    The information we collect includes:
                  </p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>
                      <strong>Log and Usage Data:</strong> Service-related,
                      diagnostic usage and performance information our servers
                      automatically collect (IP address, device info, browser
                      type, activity logs, error reports).
                    </li>
                  </ul>
                </div>

                {/* App Info */}
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    Information collected through our App
                  </h3>
                  <div className="bg-blue-50 p-4 rounded-xl mb-4 text-blue-800 text-sm font-medium border border-blue-100">
                    <strong>In Short:</strong> We collect information regarding
                    your push notifications, when you use our App.
                  </div>
                  <p className="mb-3">
                    If you use our App, we also collect the following
                    information:
                  </p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>
                      <strong>Push Notifications:</strong> We may request to
                      send you push notifications regarding your account or
                      certain features of the App. If you wish to opt-out from
                      receiving these types of communications, you may turn them
                      off in your device's settings.
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 2. Social Logins */}
            <section id="social-logins" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-slate-900 mb-4">
                2. How Do We Handle Your Social Logins?
              </h2>
              <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm text-slate-600 leading-relaxed">
                <div className="bg-blue-50 p-4 rounded-xl mb-6 text-blue-800 text-sm font-medium border border-blue-100">
                  <strong>In Short:</strong> If you choose to register or log in
                  to our services using a social media account, we may have
                  access to certain information about you.
                </div>
                <p className="mb-4">
                  Our Services offers you the ability to register and login
                  using your third-party social media account details (like your
                  Facebook or Twitter logins). Where you choose to do this, we
                  will receive certain profile information about you from your
                  social media provider. The profile Information we receive may
                  vary depending on the social media provider concerned, but
                  will often include your name, email address, friends list,
                  profile picture as well as other information you choose to
                  make public on such social media platform.
                </p>
                <p>
                  We will use the information we receive only for the purposes
                  that are described in this privacy notice or that are
                  otherwise made clear to you on the relevant Services. Please
                  note that we do not control, and are not responsible for,
                  other uses of your personal information by your third-party
                  social media provider. We recommend that you review their
                  privacy notice to understand how they collect, use and share
                  your personal information.
                </p>
              </div>
            </section>

            {/* 3. Third Party Websites */}
            <section id="third-party" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-slate-900 mb-4">
                3. What Is Our Stance On Third-Party Websites?
              </h2>
              <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm text-slate-600 leading-relaxed">
                <div className="bg-blue-50 p-4 rounded-xl mb-6 text-blue-800 text-sm font-medium border border-blue-100">
                  <strong>In Short:</strong> We are not responsible for the
                  safety of any information that you share with third-party
                  providers who advertise, but are not affiliated with, our
                  Website.
                </div>
                <p>
                  The Services may contain advertisements from third parties
                  that are not affiliated with us and which may link to other
                  websites, online services or mobile applications. We cannot
                  guarantee the safety and privacy of data you provide to any
                  third parties. Any data collected by third parties is not
                  covered by this privacy notice. We are not responsible for the
                  content or privacy and security practices and policies of any
                  third parties, including other websites, services or
                  applications that may be linked to or from the Services.
                </p>
              </div>
            </section>

            {/* 4. Data Retention */}
            <section id="retention" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-slate-900 mb-4">
                4. How Long Do We Keep Your Information?
              </h2>
              <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm text-slate-600 leading-relaxed">
                <div className="bg-blue-50 p-4 rounded-xl mb-6 text-blue-800 text-sm font-medium border border-blue-100">
                  <strong>In Short:</strong> We keep your information for as
                  long as necessary to fulfill the purposes outlined in this
                  privacy notice unless otherwise required by law.
                </div>
                <p className="mb-4">
                  We will only keep your personal information for as long as it
                  is necessary for the purposes set out in this privacy notice,
                  unless a longer retention period is required or permitted by
                  law (such as tax, accounting or other legal requirements).{" "}
                  <span className="font-bold text-slate-900">
                    No purpose in this notice will require us keeping your
                    personal information for longer than 2 years.
                  </span>
                </p>
                <p>
                  When we have no ongoing legitimate business need to process
                  your personal information, we will either delete or anonymize
                  such information, or, if this is not possible (for example,
                  because your personal information has been stored in backup
                  archives), then we will securely store your personal
                  information and isolate it from any further processing until
                  deletion is possible.
                </p>
              </div>
            </section>

            {/* 5. Data Security */}
            <section id="security" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-slate-900 mb-4">
                5. How Do We Keep Your Information Safe?
              </h2>
              <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm text-slate-600 leading-relaxed">
                <div className="bg-blue-50 p-4 rounded-xl mb-6 text-blue-800 text-sm font-medium border border-blue-100">
                  <strong>In Short:</strong> We aim to protect your personal
                  information through a system of organisational and technical
                  security measures.
                </div>
                <p>
                  We have implemented appropriate technical and organisational
                  security measures designed to protect the security of any
                  personal information we process. However, despite our
                  safeguards and efforts to secure your information, no
                  electronic transmission over the Internet or information
                  storage technology can be guaranteed to be 100% secure, so we
                  cannot promise or guarantee that hackers, cybercriminals, or
                  other unauthorised third parties will not be able to defeat
                  our security. Although we will do our best to protect your
                  personal information, transmission of personal information to
                  and from our Services is at your own risk. You should only
                  access the Services within a secure environment.
                </p>
              </div>
            </section>

            {/* 6. Privacy Rights */}
            <section id="rights" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-slate-900 mb-4">
                6. What Are Your Privacy Rights?
              </h2>
              <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm text-slate-600 leading-relaxed">
                <div className="bg-blue-50 p-4 rounded-xl mb-6 text-blue-800 text-sm font-medium border border-blue-100">
                  <strong>In Short:</strong> You may review, change, or
                  terminate your account at any time.
                </div>
                <p className="mb-4">
                  If you are resident in the European Economic Area and you
                  believe we are unlawfully processing your personal
                  information, you also have the right to complain to your local
                  data protection supervisory authority. You can find their
                  contact details here:{" "}
                  <a
                    href="http://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm"
                    className="text-blue-600 hover:underline"
                  >
                    EU Data Protection Authorities
                  </a>
                </p>
                <p>
                  If you are resident in Switzerland, the contact details for
                  the data protection authorities are available here:{" "}
                  <a
                    href="https://www.edoeb.admin.ch/edoeb/en/home.html"
                    className="text-blue-600 hover:underline"
                  >
                    FDPIC Switzerland
                  </a>
                </p>
              </div>
            </section>

            {/* 7. Do Not Track */}
            <section id="dnt" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-slate-900 mb-4">
                7. Controls For Do-Not-Track Features
              </h2>
              <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm text-slate-600 leading-relaxed">
                <p className="mb-4">
                  Most web browsers and some mobile operating systems and mobile
                  applications include a Do-Not-Track ("DNT") feature or setting
                  you can activate to signal your privacy preference not to have
                  data about your online browsing activities monitored and
                  collected. At this stage, no uniform technology standard for
                  recognizing and implementing DNT signals has been finalized.
                  As such, we do not currently respond to DNT browser signals or
                  any other mechanism that automatically communicates your
                  choice not to be tracked online. If a standard for online
                  tracking is adopted that we must follow in the future, we will
                  inform you about that practice in a revised version of this
                  privacy notice.
                </p>
              </div>
            </section>

            {/* 8. California Rights */}
            <section id="california" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-slate-900 mb-4">
                8. Do California Residents Have Specific Privacy Rights?
              </h2>
              <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm text-slate-600 leading-relaxed">
                <div className="bg-blue-50 p-4 rounded-xl mb-6 text-blue-800 text-sm font-medium border border-blue-100">
                  <strong>In Short:</strong> Yes, if you are a resident of
                  California, you are granted specific rights regarding access
                  to your personal information.
                </div>
                <p className="mb-4">
                  California Civil Code Section 1798.83, also known as the
                  "Shine The Light" law, permits our users who are California
                  residents to request and obtain from us, once a year and free
                  of charge, information about categories of personal
                  information (if any) we disclosed to third parties for direct
                  marketing purposes and the names and addresses of all third
                  parties with which we shared personal information in the
                  immediately preceding calendar year.
                </p>
                <p>
                  If you are under 18 years of age, reside in California, and
                  have a registered account with a Service, you have the right
                  to request removal of unwanted data that you publicly post on
                  the Services. To request removal of such data, please contact
                  us using the contact information provided below, and include
                  the email address associated with your account and a statement
                  that you reside in California. We will make sure the data is
                  not publicly displayed on the Services, but please be aware
                  that the data may not be completely or comprehensively removed
                  from all our systems (e.g. backups, etc.).
                </p>
              </div>
            </section>

            {/* 9. Updates */}
            <section id="updates" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-slate-900 mb-4">
                9. Do We Make Updates To This Notice?
              </h2>
              <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm text-slate-600 leading-relaxed">
                <div className="bg-blue-50 p-4 rounded-xl mb-6 text-blue-800 text-sm font-medium border border-blue-100">
                  <strong>In Short:</strong> Yes, we will update this notice as
                  necessary to stay compliant with relevant laws.
                </div>
                <p>
                  We may update this privacy notice from time to time. The
                  updated version will be indicated by an updated "Revised" date
                  and the updated version will be effective as soon as it is
                  accessible. If we make material changes to this privacy
                  notice, we may notify you either by prominently posting a
                  notice of such changes or by directly sending you a
                  notification. We encourage you to review this privacy notice
                  frequently to be informed of how we are protecting your
                  information.
                </p>
              </div>
            </section>

            {/* 10. Contact */}
            <section id="contact" className="scroll-mt-28">
              <h2 className="text-2xl font-black text-slate-900 mb-4">
                10. Contact Us
              </h2>
              <div className="bg-slate-900 text-white p-8 rounded-3xl shadow-lg relative overflow-hidden">
                <div className="relative z-10">
                  <p className="mb-6 font-medium text-slate-300">
                    If you have questions or comments about this policy, you may
                    email us or contact us by post at:
                  </p>
                  <div className="flex flex-col gap-4">
                    <a
                      href="mailto:contact@freshmentapp.com"
                      className="flex items-center flex-wrap  gap-3 bg-white/10 hover:bg-white/20 p-4 rounded-xl transition-colors"
                    >
                      <div className="bg-white text-slate-900 p-2 rounded-lg">
                        <FileText className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="text-xs text-slate-400 font-bold uppercase">
                          Email Support
                        </div>
                        <div className="font-bold break-all wrap-anywhere">
                          contact@freshmentapp.com
                        </div>
                      </div>
                    </a>
                    <div className="p-4 rounded-xl border border-white/10">
                      <div className="text-xs text-slate-400 font-bold uppercase mb-1">
                        Mailing Address
                      </div>
                      <address className="not-italic text-slate-200 text-sm leading-relaxed">
                        GradConnect Innovations Private Limited
                        <br />
                        B-519, RK Iconic Sheetal Park,
                        <br />
                        Puneet Naga Bajrang, Rajkot Bhomeshwar Plot,
                        <br />
                        Rajkot- 360006, Gujarat, India
                      </address>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
